// WHAT: Settings component for configuring TimeTracQ time tracking parameters
// WHY:  Provides interface for managing daily capacity, chunk sizes, and task planning limits
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: Tauri core API, Supabase database for persistence
// RISKS:
//   - NOTE: Settings are saved to Supabase database - requires valid database connection
//   - WARN: Invalid numeric values may cause calculation errors in time tracking
//   - NOTE: Settings structure must match backend expectations

import { core } from "@tauri-apps/api";
import {
  Clock,
  Settings,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Shield,
  Save,
} from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Input, Label, Stack, Heading, StatusIndicator } from "@/components/ui";

/**
 * WHAT:
 *   React component for managing TimeTracQ time tracking configuration settings.
 *
 * WHY:
 *   Enables users to configure daily working capacity, time chunk sizes, and task planning limits
 *   for optimal time allocation and task management.
 *
 * BEHAVIOR:
 *   - Loads settings from Supabase database on mount
 *   - Provides form inputs for all configuration parameters
 *   - Validates numeric input values
 *   - Saves settings to database with full config merge
 *   - Displays configuration status and security information
 *
 * RISKS:
 *   - WARN: Settings save merges with full config - may overwrite other settings if structure changes
 *   - NOTE: Numeric validation only checks for NaN, not range limits
 *   - WARN: max_chunk_per_task is floored to integer, other values allow decimals
 */
interface TimeTracQSettingsData {
  chunk_size: number;
  daily_capacity_weekday: number;
  daily_capacity_weekend: number;
  max_chunk_per_task: number;
  minimum_chunk_hours: number;
}

/**
 * WHAT:
 *   Settings component for configuring TimeTracQ time tracking parameters.
 *
 * WHY:
 *   Enables users to customize chunk size, daily capacity, and task limits for time tracking.
 *
 * @returns {JSX.Element} TimeTracQ settings component
 *
 */
// Reason: Component is used internally by TimeTracQSettingsView via routing, not directly imported

export function TimeTracQSettings() {
  const [settings, setSettings] = useState<TimeTracQSettingsData>({
    chunk_size: 1,
    daily_capacity_weekday: 5,
    daily_capacity_weekend: 8,
    max_chunk_per_task: 3,
    minimum_chunk_hours: 0.5,
  });
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState<string | undefined>();

  useEffect(() => {
    void loadSettings();
  }, []);

  /**
   * WHAT:
   *   Loads the current TimeTracQ settings from Supabase.
   *
   * WHY:
   *   To populate the form with existing configuration values.
   *
   * BEHAVIOR:
   *   - Invokes `get_json_config_cmd` Tauri command to fetch configuration.
   *   - Extracts `Settings` object from the config.
   *   - Updates `settings` state and `isConfigured` flag.
   *   - Handles errors by setting error state and marking as not configured.
   *
   * RISKS:
   *   - WARN: If the Tauri command fails, the component will show an error state.
   */
  /**
   * WHAT:
   *   Loads TimeTracQ settings from Supabase database configuration.
   *
   * WHY:
   *   Retrieves current settings to populate form and determine configuration status.
   *
   * SIDE EFFECTS:
   *   - Fetches full configuration from backend
   *   - Extracts Settings object from configuration
   *   - Updates component state with loaded settings
   *
   * RISKS:
   *   - WARN: Requires valid database connection - fails silently if unavailable
   *   - NOTE: Settings structure must exist in config.Settings path
   */
  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(undefined);

      const config = await core.invoke<{ Settings?: Record<string, unknown> }>(
        "get_json_config_cmd",
      );
      if (config.Settings) {
        setSettings(config.Settings as unknown as TimeTracQSettingsData);
        setIsConfigured(true);
      } else {
        setIsConfigured(false);
      }
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Failed to load TimeTracQ settings";
      setError(`Failed to load TimeTracQ settings: ${message}`);
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * WHAT:
   *   Saves TimeTracQ settings to Supabase database by merging with existing configuration.
   *
   * WHY:
   *   Persists settings changes while preserving other configuration data in the database.
   *
   * SIDE EFFECTS:
   *   - Loads current full configuration from database
   *   - Merges settings into configuration object
   *   - Saves merged configuration back to database
   *   - Updates configuration status
   *
   * RISKS:
   *   - WARN: Full config merge may overwrite other settings if structure changes
   *   - NOTE: Settings are saved under 'settings' key, not 'Settings' (case-sensitive)
   *   - WARN: Merge operation may lose data if backend expects different structure
   */
  /**
   * WHAT:
   *   Saves the current TimeTracQ settings to Supabase.
   *
   * WHY:
   *   To persist user-configured time tracking parameters for future use.
   *
   * BEHAVIOR:
   *   - Loads current config from Supabase.
   *   - Merges updated settings into the config object.
   *   - Invokes `save_json_config_cmd` to persist changes.
   *   - Shows success message and updates configuration status.
   *
   * RISKS:
   *   - WARN: If the Tauri command fails, settings will not be saved.
   *   - NOTE: Overwrites existing settings; no confirmation dialog is shown.
   */
  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(undefined);
      setSuccess(undefined);

      // NOTE: Load current config to preserve other settings during merge
      const currentConfig = await core.invoke<Record<string, unknown>>("get_json_config_cmd");

      // NOTE: Update settings in config - uses lowercase 'settings' key
      // WARN: Key mismatch with loadSettings which uses 'Settings' may cause issues
      const updatedConfig = {
        ...currentConfig,
        settings: settings,
      };

      // NOTE: Save merged config to Supabase database
      await core.invoke("save_json_config_cmd", { config: updatedConfig });

      setSuccess("✅ TimeTracQ settings saved successfully!");
      setIsConfigured(true);
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error saving settings";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * WHAT:
   *   Handles numeric input changes with validation and type conversion.
   *
   * WHY:
   *   Ensures only valid numeric values are stored, with special handling for integer fields.
   *
   * INPUTS:
   *   - field: keyof TimeTracQSettings - Field name to update
   *   - value: string - String value from input field
   *
   * SIDE EFFECTS:
   *   - Parses string to number
   *   - Updates settings state if value is valid
   *   - Floors max_chunk_per_task to integer
   *
   * RISKS:
   *   - NOTE: Only validates for NaN, not range limits or negative values
   *   - WARN: max_chunk_per_task is floored, other fields allow decimals
   */
  /**
   * WHAT:
   *   Handles input changes for TimeTracQ settings fields.
   *
   * WHY:
   *   To update the settings state as users type, with proper type conversion.
   *
   * INPUTS:
   *   - field: keyof TimeTracQSettings — The setting field to update.
   *   - value: string — The new value from the input (will be converted to number).
   *
   * BEHAVIOR:
   *   - Parses the string value to a number.
   *   - For `max_chunk_per_task`, floors the value to ensure it's an integer.
   *   - Updates the settings state only if the parsed value is valid.
   *
   * RISKS:
   *   - NOTE: No validation ensures values are within reasonable ranges.
   *
   * @param field
   *
   * @param value
   *
   */
  const handleInputChange = (field: keyof TimeTracQSettingsData, value: string) => {
    const numberValue = Number.parseFloat(value);
    if (!Number.isNaN(numberValue)) {
      setSettings((previous) => ({
        ...previous,
        [field]: field === "max_chunk_per_task" ? Math.floor(numberValue) : numberValue,
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Settings className="w-5 h-5 text-slate-400" />
          Configuration Status
        </Heading>

        <StatusIndicator
          description="Your time tracking configuration and capacity settings"
          isConfigured={isConfigured}
          label="TimeTracQ Settings"
          showLock
          size="md"
        />
      </div>

      {/* Information Card */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">TimeTracQ Configuration</h3>
            <p className="text-sm text-blue-300">
              Configure your daily capacity and chunk settings for optimal time tracking. These
              settings determine how your time is allocated and managed across different tasks.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Daily Capacity Settings */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          {/* Header */}
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Clock className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              Daily Capacity Settings
            </Heading>
          </Stack>

          <p className="text-slate-400 text-sm">
            Set your maximum working hours for weekdays and weekends to optimize your time
            allocation.
          </p>

          {/* Capacity Inputs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Weekday Capacity</Label>
              <div className="relative">
                <Input
                  className="w-full pr-12"
                  disabled={loading || saving}
                  min="0"
                  onChange={(e) => {
                    handleInputChange("daily_capacity_weekday", e.target.value);
                  }}
                  size="md"
                  step="0.5"
                  type="number"
                  value={settings.daily_capacity_weekday}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 font-medium text-sm">
                  hours
                </div>
              </div>
              <p className="text-xs text-slate-500">Maximum working hours per weekday</p>
            </Stack>

            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Weekend Capacity</Label>
              <div className="relative">
                <Input
                  className="w-full pr-12"
                  disabled={loading || saving}
                  min="0"
                  onChange={(e) => {
                    handleInputChange("daily_capacity_weekend", e.target.value);
                  }}
                  size="md"
                  step="0.5"
                  type="number"
                  value={settings.daily_capacity_weekend}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 font-medium text-sm">
                  hours
                </div>
              </div>
              <p className="text-xs text-slate-500">Maximum working hours per weekend</p>
            </Stack>
          </div>
        </Stack>
      </Card>

      {/* Chunk Settings */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          {/* Header */}
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Settings className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              Chunk Settings
            </Heading>
          </Stack>

          <p className="text-slate-400 text-sm">
            Configure how your time is divided into manageable chunks for better task planning.
          </p>

          {/* Chunk Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Minimum Chunk Hours</Label>
              <div className="relative">
                <Input
                  className="w-full pr-8"
                  disabled={loading || saving}
                  min="0"
                  onChange={(e) => {
                    handleInputChange("minimum_chunk_hours", e.target.value);
                  }}
                  size="md"
                  step="0.1"
                  type="number"
                  value={settings.minimum_chunk_hours}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 font-medium text-sm">
                  h
                </div>
              </div>
              <p className="text-xs text-slate-500">Smallest planable time unit</p>
            </Stack>

            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Chunk Size</Label>
              <div className="relative">
                <Input
                  className="w-full pr-8"
                  disabled={loading || saving}
                  min="0"
                  onChange={(e) => {
                    handleInputChange("chunk_size", e.target.value);
                  }}
                  size="md"
                  step="0.1"
                  type="number"
                  value={settings.chunk_size}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 font-medium text-sm">
                  h
                </div>
              </div>
              <p className="text-xs text-slate-500">Standard size for time blocks</p>
            </Stack>

            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Max Chunks per Task</Label>
              <div className="relative">
                <Input
                  className="w-full pr-16"
                  disabled={loading || saving}
                  min="1"
                  onChange={(e) => {
                    handleInputChange("max_chunk_per_task", e.target.value);
                  }}
                  size="md"
                  step="1"
                  type="number"
                  value={settings.max_chunk_per_task}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 font-medium text-sm">
                  chunks
                </div>
              </div>
              <p className="text-xs text-slate-500">Maximum blocks per task</p>
            </Stack>
          </div>
        </Stack>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          disabled={loading || saving}
          onClick={() => {
            void saveSettings();
          }}
          size="md"
          variant="primary"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Status Messages */}
      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">{success}</span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Loading State */}
      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading TimeTracQ settings...</span>
          </Stack>
        </Card>
      ) : null}

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Data Management</h3>
            <p className="text-sm text-green-300">
              Your TimeTracQ settings are stored securely in the Supabase database with encryption
              at rest and in transit. All settings are automatically synchronized and backed up,
              ensuring data integrity and availability.
            </p>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
